// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// The thumb. Everything between a finger touching glass and an order existing.
//
// ── THE GESTURE WAS DESIGNED BEFORE THE RULES ───────────────────────────────────────────────────
// A tactics game on a phone dies at the input layer or not at all, so this is the part that had to
// work first. Two rules shape all of it:
//
//   THE DRAG DOES NOT HAVE TO START ON THE UNIT. You select a unit by tapping its chip in the dock —
//   a target that never moves and never needs board precision — and then drag ANYWHERE on the
//   board; the route grows from the unit toward your finger. That is what kills the reach-across
//   problem: at a 29px cell your thumb would otherwise have to land on, and then cover, the exact
//   thing you are trying to look at.
//
//   THE PATH FOLLOWS THE GESTURE, NOT THE TILES. As the finger moves, the route keeps a list of
//   anchors: if the straight line from the last anchor to the finger is walkable, the route is
//   anchors + finger; if it is not, the PREVIOUS position becomes a permanent anchor and the route
//   bends there. So dragging around a wall produces a corner at the wall and dragging straight
//   produces a straight line, from a scribble, with no tapping of individual waypoints.
//
// Pointer Events only, `setPointerCapture` on down, per-pointer state, and `pointercancel` handled
// like an abort — a phone call arriving mid-drag must not leave a half-drawn order on the board.

import { MAX_WAYPOINTS } from './tuning';
import { walkClear, type SideOrders, type Stance } from './orders';
import { sectorOf } from './rules';
import { tileAt, type View } from './render';
import { blocksFeet, tx, ty, type GameMap } from './map';

export type Grab = null | { kind: 'path'; pointer: number } | { kind: 'watch'; pointer: number };

export interface Planner {
  selected: number;
  grab: Grab;
}

/**
 * Extend a route toward a tile, keeping it legal and simplified.
 *
 * Returns the new anchor list. The last entry is provisional — it tracks the finger and is replaced
 * on the next move — and everything before it is committed, which is what makes a corner appear
 * exactly where the wall made the straight line impossible.
 */
export function extendPath(map: GameMap, from: number, anchors: number[], finger: number): number[] {
  if (finger < 0 || blocksFeet(map, tx(finger), ty(finger))) return anchors;
  const fixed = anchors.slice(0, Math.max(0, anchors.length - 1));
  const last = fixed.length ? fixed[fixed.length - 1] : from;
  if (finger === last) return fixed;
  if (walkClear(map, last, finger)) return [...fixed, finger];

  // The straight line is blocked, so the previous provisional point becomes permanent and the route
  // bends there — but only if that point is itself reachable and we have anchors to spare.
  const prev = anchors.length ? anchors[anchors.length - 1] : from;
  if (fixed.length >= MAX_WAYPOINTS || prev === last || !walkClear(map, last, prev)) return anchors;
  const bent = [...fixed, prev];
  return walkClear(map, prev, finger) && bent.length < MAX_WAYPOINTS ? [...bent, finger] : bent;
}

/** Where the watch handle sits: a fixed distance out from the route's end, along the watch. */
export function handleAt(
  v: View,
  end: { x: number; y: number },
  watch: number,
  dirs: ReadonlyArray<readonly [number, number]>,
): { x: number; y: number } {
  const d = dirs[watch] ?? dirs[0];
  const out = Math.max(38, v.cell * 1.5);
  return { x: v.x + end.x * v.cell + (d[0] / 1024) * out, y: v.y + end.y * v.cell + (d[1] / 1024) * out };
}

/** The sector a finger at (px, py) asks for, measured from the route's end. */
export function watchFromPointer(v: View, end: { x: number; y: number }, px: number, py: number): number {
  return sectorOf(px - (v.x + end.x * v.cell), py - (v.y + end.y * v.cell));
}

/** Which unit a board tap means, or -1 — with slop, because a 29px cell is not a 44px target. */
export function unitAtPointer(
  v: View,
  units: Array<{ x: number; y: number; alive: boolean; side: number }>,
  side: number,
  px: number,
  py: number,
): number {
  let best = -1;
  let bestD = Math.max(44, v.cell * 1.4);
  units.forEach((u, i) => {
    if (u.side !== side || !u.alive) return;
    const d = Math.hypot(px - (v.x + u.x * v.cell), py - (v.y + u.y * v.cell));
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  });
  return best;
}

export { tileAt, type Stance, type SideOrders };
